
import { JavaScriptCourse } from './types';

export const JS_ADVANCED_COURSE: JavaScriptCourse = {
  id: 'js-advanced',
  title: 'Advanced JavaScript',
  description: 'Master advanced JavaScript concepts including design patterns, performance optimization, and modern development practices.',
  level: 'advanced',
  totalDuration: '12-15 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Intermediate JavaScript', 'Understanding of async/await', 'Experience with ES6+ features'],
  learningOutcomes: [
    'Implement advanced asynchronous patterns',
    'Apply design patterns effectively',
    'Optimize JavaScript performance',
    'Master testing methodologies',
    'Understand security best practices',
    'Build production-ready applications'
  ],
  modules: [
    {
      id: 'js-design-patterns',
      title: 'Design Patterns',
      description: 'Learn and implement common JavaScript design patterns',
      duration: '120 minutes',
      difficulty: 'advanced',
      objectives: [
        'Understand and implement the Module pattern',
        'Use the Observer pattern for event systems',
        'Apply Factory and Singleton patterns',
        'Create flexible code with Strategy pattern'
      ],
      content: `# JavaScript Design Patterns

## Module Pattern

The Module pattern provides encapsulation and privacy for your code.

\`\`\`javascript
// IIFE Module Pattern
const UserModule = (function() {
    // Private variables and functions
    let users = [];
    let currentId = 1;
    
    function validateUser(user) {
        return user.name && user.email;
    }
    
    function generateId() {
        return currentId++;
    }
    
    // Public API
    return {
        addUser: function(name, email) {
            const user = { id: generateId(), name, email };
            
            if (validateUser(user)) {
                users.push(user);
                return user;
            }
            throw new Error('Invalid user data');
        },
        
        getUser: function(id) {
            return users.find(user => user.id === id);
        },
        
        getAllUsers: function() {
            return [...users]; // Return copy to prevent external modification
        },
        
        removeUser: function(id) {
            const index = users.findIndex(user => user.id === id);
            if (index !== -1) {
                return users.splice(index, 1)[0];
            }
            return null;
        }
    };
})();

// Usage
let user = UserModule.addUser('John Doe', 'john@example.com');
console.log(UserModule.getAllUsers());
\`\`\`

## Observer Pattern

Allows objects to subscribe to and receive notifications about events.

\`\`\`javascript
class EventEmitter {
    constructor() {
        this.events = {};
    }
    
    on(event, callback) {
        if (!this.events[event]) {
            this.events[event] = [];
        }
        this.events[event].push(callback);
    }
    
    off(event, callback) {
        if (this.events[event]) {
            this.events[event] = this.events[event].filter(cb => cb !== callback);
        }
    }
    
    emit(event, data) {
        if (this.events[event]) {
            this.events[event].forEach(callback => callback(data));
        }
    }
}

// Example: Shopping Cart with Observer Pattern
class ShoppingCart extends EventEmitter {
    constructor() {
        super();
        this.items = [];
        this.total = 0;
    }
    
    addItem(item) {
        this.items.push(item);
        this.total += item.price;
        this.emit('itemAdded', { item, total: this.total });
    }
    
    removeItem(itemId) {
        const index = this.items.findIndex(item => item.id === itemId);
        if (index !== -1) {
            const removedItem = this.items.splice(index, 1)[0];
            this.total -= removedItem.price;
            this.emit('itemRemoved', { item: removedItem, total: this.total });
        }
    }
}

// Usage
const cart = new ShoppingCart();

cart.on('itemAdded', ({ item, total }) => {
    console.log(\`Added \${item.name} to cart. Total: $\${total}\`);
});

cart.on('itemRemoved', ({ item, total }) => {
    console.log(\`Removed \${item.name} from cart. Total: $\${total}\`);
});

cart.addItem({ id: 1, name: 'Laptop', price: 999.99 });
cart.addItem({ id: 2, name: 'Mouse', price: 29.99 });
\`\`\`

## Factory Pattern

Creates objects without specifying their exact classes.

\`\`\`javascript
// Abstract Factory for UI Components
class ComponentFactory {
    static createComponent(type, props = {}) {
        switch (type) {
            case 'button':
                return new Button(props);
            case 'input':
                return new Input(props);
            case 'modal':
                return new Modal(props);
            default:
                throw new Error(\`Component type "\${type}" not supported\`);
        }
    }
}

class Button {
    constructor({ text = 'Click me', onClick = () => {}, className = '' }) {
        this.text = text;
        this.onClick = onClick;
        this.className = className;
        this.element = this.createElement();
    }
    
    createElement() {
        const button = document.createElement('button');
        button.textContent = this.text;
        button.className = this.className;
        button.addEventListener('click', this.onClick);
        return button;
    }
    
    render(container) {
        container.appendChild(this.element);
    }
}

class Input {
    constructor({ placeholder = '', type = 'text', onChange = () => {} }) {
        this.placeholder = placeholder;
        this.type = type;
        this.onChange = onChange;
        this.element = this.createElement();
    }
    
    createElement() {
        const input = document.createElement('input');
        input.placeholder = this.placeholder;
        input.type = this.type;
        input.addEventListener('input', this.onChange);
        return input;
    }
    
    render(container) {
        container.appendChild(this.element);
    }
}

// Usage
const submitButton = ComponentFactory.createComponent('button', {
    text: 'Submit',
    onClick: () => console.log('Form submitted'),
    className: 'btn btn-primary'
});

const emailInput = ComponentFactory.createComponent('input', {
    placeholder: 'Enter your email',
    type: 'email',
    onChange: (e) => console.log('Email:', e.target.value)
});
\`\`\`

## Singleton Pattern

Ensures only one instance of a class exists.

\`\`\`javascript
class DatabaseConnection {
    constructor() {
        if (DatabaseConnection.instance) {
            return DatabaseConnection.instance;
        }
        
        this.connection = null;
        this.isConnected = false;
        DatabaseConnection.instance = this;
    }
    
    connect(connectionString) {
        if (!this.isConnected) {
            console.log(\`Connecting to database: \${connectionString}\`);
            this.connection = { url: connectionString, status: 'connected' };
            this.isConnected = true;
        }
        return this.connection;
    }
    
    disconnect() {
        if (this.isConnected) {
            console.log('Disconnecting from database');
            this.connection = null;
            this.isConnected = false;
        }
    }
    
    query(sql) {
        if (!this.isConnected) {
            throw new Error('Not connected to database');
        }
        console.log(\`Executing query: \${sql}\`);
        return { results: 'Mock data' };
    }
}

// Usage
const db1 = new DatabaseConnection();
const db2 = new DatabaseConnection();

console.log(db1 === db2); // true - same instance

db1.connect('postgresql://localhost:5432/mydb');
console.log(db2.isConnected); // true - shared state
\`\`\`

## Strategy Pattern

Defines a family of algorithms and makes them interchangeable.

\`\`\`javascript
// Payment strategies
class CreditCardPayment {
    pay(amount) {
        console.log(\`Paid $\${amount} using Credit Card\`);
        return { success: true, method: 'credit_card', amount };
    }
}

class PayPalPayment {
    pay(amount) {
        console.log(\`Paid $\${amount} using PayPal\`);
        return { success: true, method: 'paypal', amount };
    }
}

class CryptoPayment {
    pay(amount) {
        console.log(\`Paid $\${amount} using Cryptocurrency\`);
        return { success: true, method: 'crypto', amount };
    }
}

// Context class
class PaymentProcessor {
    constructor(strategy) {
        this.strategy = strategy;
    }
    
    setStrategy(strategy) {
        this.strategy = strategy;
    }
    
    processPayment(amount) {
        if (!this.strategy) {
            throw new Error('No payment strategy set');
        }
        return this.strategy.pay(amount);
    }
}

// Usage
const processor = new PaymentProcessor(new CreditCardPayment());
processor.processPayment(99.99);

processor.setStrategy(new PayPalPayment());
processor.processPayment(149.99);

processor.setStrategy(new CryptoPayment());
processor.processPayment(199.99);
\`\`\``,
      practice: {
        task: 'Build a notification system using multiple design patterns',
        starter: `// Build a comprehensive notification system that:
// 1. Uses Factory pattern to create different notification types
// 2. Uses Observer pattern for subscription management
// 3. Uses Strategy pattern for different delivery methods
// 4. Uses Singleton pattern for notification manager

// Add your implementation here`,
        solution: `// Notification System with Multiple Design Patterns

// Singleton Notification Manager
class NotificationManager {
    constructor() {
        if (NotificationManager.instance) {
            return NotificationManager.instance;
        }
        
        this.subscribers = new Map();
        this.deliveryStrategies = new Map();
        this.notificationHistory = [];
        
        // Register default delivery strategies
        this.registerDeliveryStrategy('email', new EmailDelivery());
        this.registerDeliveryStrategy('sms', new SMSDelivery());
        this.registerDeliveryStrategy('push', new PushDelivery());
        
        NotificationManager.instance = this;
    }
    
    // Observer pattern methods
    subscribe(userId, notificationType, callback) {
        const key = \`\${userId}:\${notificationType}\`;
        if (!this.subscribers.has(key)) {
            this.subscribers.set(key, []);
        }
        this.subscribers.get(key).push(callback);
    }
    
    unsubscribe(userId, notificationType, callback) {
        const key = \`\${userId}:\${notificationType}\`;
        if (this.subscribers.has(key)) {
            const callbacks = this.subscribers.get(key);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        }
    }
    
    // Strategy pattern for delivery methods
    registerDeliveryStrategy(name, strategy) {
        this.deliveryStrategies.set(name, strategy);
    }
    
    async sendNotification(notification, deliveryMethod = 'email') {
        // Get delivery strategy
        const strategy = this.deliveryStrategies.get(deliveryMethod);
        if (!strategy) {
            throw new Error(\`Delivery method "\${deliveryMethod}" not supported\`);
        }
        
        // Send notification using strategy
        const result = await strategy.deliver(notification);
        
        // Store in history
        this.notificationHistory.push({
            ...notification,
            deliveryMethod,
            sentAt: new Date(),
            result
        });
        
        // Notify subscribers
        this.notifySubscribers(notification);
        
        return result;
    }
    
    notifySubscribers(notification) {
        const key = \`\${notification.userId}:\${notification.type}\`;
        if (this.subscribers.has(key)) {
            this.subscribers.get(key).forEach(callback => {
                try {
                    callback(notification);
                } catch (error) {
                    console.error('Subscriber callback error:', error);
                }
            });
        }
    }
    
    getHistory(userId = null) {
        if (userId) {
            return this.notificationHistory.filter(n => n.userId === userId);
        }
        return [...this.notificationHistory];
    }
}

// Factory pattern for creating notifications
class NotificationFactory {
    static createNotification(type, data) {
        switch (type) {
            case 'welcome':
                return new WelcomeNotification(data);
            case 'order':
                return new OrderNotification(data);
            case 'reminder':
                return new ReminderNotification(data);
            case 'promotion':
                return new PromotionNotification(data);
            default:
                throw new Error(\`Notification type "\${type}" not supported\`);
        }
    }
}

// Base notification class
class BaseNotification {
    constructor({ userId, title, message, data = {} }) {
        this.id = this.generateId();
        this.userId = userId;
        this.title = title;
        this.message = message;
        this.data = data;
        this.createdAt = new Date();
    }
    
    generateId() {
        return \`notif_\${Date.now()}_\${Math.random().toString(36).substr(2, 9)}\`;
    }
}

// Specific notification types
class WelcomeNotification extends BaseNotification {
    constructor(data) {
        super({
            ...data,
            title: \`Welcome, \${data.userName}!\`,
            message: 'Thank you for joining our platform. Get started with your first course!'
        });
        this.type = 'welcome';
    }
}

class OrderNotification extends BaseNotification {
    constructor(data) {
        super({
            ...data,
            title: 'Order Confirmation',
            message: \`Your order #\${data.orderId} has been confirmed.\`
        });
        this.type = 'order';
    }
}

class ReminderNotification extends BaseNotification {
    constructor(data) {
        super({
            ...data,
            title: 'Reminder',
            message: data.message || 'You have a pending task.'
        });
        this.type = 'reminder';
    }
}

class PromotionNotification extends BaseNotification {
    constructor(data) {
        super({
            ...data,
            title: 'Special Offer!',
            message: data.message || 'Don\'t miss out on our latest promotion!'
        });
        this.type = 'promotion';
    }
}

// Strategy pattern for delivery methods
class EmailDelivery {
    async deliver(notification) {
        console.log(\`📧 Sending email to user \${notification.userId}\`);
        console.log(\`Subject: \${notification.title}\`);
        console.log(\`Body: \${notification.message}\`);
        
        // Simulate async email delivery
        return new Promise(resolve => {
            setTimeout(() => {
                resolve({
                    success: true,
                    deliveryId: \`email_\${Date.now()}\`,
                    deliveredAt: new Date()
                });
            }, Math.random() * 1000 + 500);
        });
    }
}

class SMSDelivery {
    async deliver(notification) {
        console.log(\`📱 Sending SMS to user \${notification.userId}\`);
        console.log(\`Message: \${notification.message}\`);
        
        return new Promise(resolve => {
            setTimeout(() => {
                resolve({
                    success: true,
                    deliveryId: \`sms_\${Date.now()}\`,
                    deliveredAt: new Date()
                });
            }, Math.random() * 500 + 200);
        });
    }
}

class PushDelivery {
    async deliver(notification) {
        console.log(\`🔔 Sending push notification to user \${notification.userId}\`);
        console.log(\`Title: \${notification.title}\`);
        console.log(\`Body: \${notification.message}\`);
        
        return new Promise(resolve => {
            setTimeout(() => {
                resolve({
                    success: true,
                    deliveryId: \`push_\${Date.now()}\`,
                    deliveredAt: new Date()
                });
            }, Math.random() * 300 + 100);
        });
    }
}

// Usage Example
async function demonstrateNotificationSystem() {
    const manager = new NotificationManager();
    
    // Subscribe to notifications
    manager.subscribe('user123', 'welcome', (notification) => {
        console.log('User received welcome notification:', notification.title);
    });
    
    manager.subscribe('user123', 'order', (notification) => {
        console.log('User received order notification:', notification.title);
    });
    
    // Create and send notifications
    const welcomeNotification = NotificationFactory.createNotification('welcome', {
        userId: 'user123',
        userName: 'John Doe'
    });
    
    const orderNotification = NotificationFactory.createNotification('order', {
        userId: 'user123',
        orderId: 'ORD-2024-001'
    });
    
    // Send via different delivery methods
    await manager.sendNotification(welcomeNotification, 'email');
    await manager.sendNotification(orderNotification, 'sms');
    
    // Check history
    console.log('Notification History:', manager.getHistory('user123'));
}

// Run demonstration
demonstrateNotificationSystem();`,
        hints: [
          'Use Singleton pattern for the main NotificationManager',
          'Implement Observer pattern for user subscriptions',
          'Create Factory pattern for different notification types',
          'Use Strategy pattern for delivery methods (email, SMS, push)',
          'Store notification history and handle errors gracefully'
        ]
      }
    },
    {
      id: 'js-performance-optimization',
      title: 'Performance Optimization',
      description: 'Learn techniques to optimize JavaScript performance and memory usage',
      duration: '100 minutes',
      difficulty: 'advanced',
      objectives: [
        'Understand JavaScript performance bottlenecks',
        'Optimize memory usage and prevent memory leaks',
        'Use performance measurement tools',
        'Implement lazy loading and code splitting'
      ],
      content: `# JavaScript Performance Optimization

## Understanding Performance

Performance optimization in JavaScript involves making your code run faster and use less memory.

## Memory Management

\`\`\`javascript
// Memory leak example - DON'T DO THIS
let globalArray = [];

function addToGlobalArray(data) {
    globalArray.push(data); // Memory keeps growing
}

// Better approach - cleanup when done
class DataProcessor {
    constructor() {
        this.tempData = [];
    }
    
    processData(data) {
        this.tempData.push(data);
        // Process the data
        let result = this.tempData.map(item => item.value).reduce((a, b) => a + b, 0);
        
        // Clean up temporary data
        this.tempData = [];
        return result;
    }
}

// Remove event listeners to prevent memory leaks
function setupEventListener() {
    const button = document.getElementById('myButton');
    
    function handleClick() {
        console.log('Button clicked');
    }
    
    button.addEventListener('click', handleClick);
    
    // Clean up when component is destroyed
    return function cleanup() {
        button.removeEventListener('click', handleClick);
    };
}
\`\`\`

## Performance Measurement

\`\`\`javascript
// Basic performance timing
function measurePerformance(fn, label) {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    console.log(\`\${label} took \${end - start} milliseconds\`);
    return result;
}

// More detailed performance monitoring
class PerformanceMonitor {
    constructor() {
        this.metrics = new Map();
    }
    
    startTimer(label) {
        this.metrics.set(label, {
            startTime: performance.now(),
            startMemory: performance.memory ? performance.memory.usedJSHeapSize : 0
        });
    }
    
    endTimer(label) {
        const metric = this.metrics.get(label);
        if (!metric) {
            console.warn(\`Timer '\${label}' was not started\`);
            return;
        }
        
        const endTime = performance.now();
        const endMemory = performance.memory ? performance.memory.usedJSHeapSize : 0;
        
        const result = {
            duration: endTime - metric.startTime,
            memoryDelta: endMemory - metric.startMemory,
            label: label
        };
        
        console.log(\`Performance - \${label}:\`, result);
        this.metrics.delete(label);
        return result;
    }
}

// Usage
const monitor = new PerformanceMonitor();

monitor.startTimer('arrayProcessing');
let largeArray = new Array(1000000).fill(0).map((_, i) => i);
let sum = largeArray.reduce((a, b) => a + b, 0);
monitor.endTimer('arrayProcessing');
\`\`\`

## Optimizing Loops and Array Operations

\`\`\`javascript
// Inefficient approach
function processDataSlow(data) {
    let result = [];
    
    // Multiple passes through the array
    let filtered = data.filter(item => item.active);
    let mapped = filtered.map(item => ({ ...item, processed: true }));
    let sorted = mapped.sort((a, b) => a.priority - b.priority);
    
    return sorted;
}

// Optimized approach - single pass
function processDataFast(data) {
    return data
        .reduce((acc, item) => {
            if (item.active) {
                acc.push({ ...item, processed: true });
            }
            return acc;
        }, [])
        .sort((a, b) => a.priority - b.priority);
}

// For very large datasets, consider using generators
function* processLargeDataset(data) {
    for (let item of data) {
        if (item.active) {
            yield { ...item, processed: true };
        }
    }
}

// Usage with generator
function processInChunks(data, chunkSize = 1000) {
    const processor = processLargeDataset(data);
    const results = [];
    
    let chunk = [];
    for (let item of processor) {
        chunk.push(item);
        
        if (chunk.length >= chunkSize) {
            results.push(...chunk);
            chunk = [];
            
            // Allow other operations to run
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(results);
                }, 0);
            });
        }
    }
    
    results.push(...chunk);
    return Promise.resolve(results);
}
\`\`\`

## Lazy Loading and Code Splitting

\`\`\`javascript
// Lazy loading modules
class ModuleLoader {
    constructor() {
        this.loadedModules = new Map();
    }
    
    async loadModule(moduleName) {
        if (this.loadedModules.has(moduleName)) {
            return this.loadedModules.get(moduleName);
        }
        
        console.log(\`Loading module: \${moduleName}\`);
        
        let module;
        switch (moduleName) {
            case 'chart':
                module = await import('./modules/chart.js');
                break;
            case 'calendar':
                module = await import('./modules/calendar.js');
                break;
            case 'editor':
                module = await import('./modules/editor.js');
                break;
            default:
                throw new Error(\`Module \${moduleName} not found\`);
        }
        
        this.loadedModules.set(moduleName, module);
        return module;
    }
}

// Lazy loading images
class LazyImageLoader {
    constructor() {
        this.observer = new IntersectionObserver(
            this.handleIntersection.bind(this),
            { threshold: 0.1 }
        );
    }
    
    observe(imageElement) {
        this.observer.observe(imageElement);
    }
    
    handleIntersection(entries) {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const img = entry.target;
                const src = img.dataset.src;
                
                if (src) {
                    img.src = src;
                    img.removeAttribute('data-src');
                    this.observer.unobserve(img);
                }
            }
        });
    }
}

// Usage
const imageLoader = new LazyImageLoader();
document.querySelectorAll('img[data-src]').forEach(img => {
    imageLoader.observe(img);
});
\`\`\`

## Debouncing and Throttling

\`\`\`javascript
// Debounce - wait for inactivity
function debounce(func, delay) {
    let timeoutId;
    
    return function debounced(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
}

// Throttle - limit execution frequency
function throttle(func, limit) {
    let inThrottle;
    
    return function throttled(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

// Advanced throttle with trailing execution
function advancedThrottle(func, limit, options = {}) {
    let timeout;
    let previous = 0;
    let result;
    
    const later = function(context, args) {
        previous = options.leading === false ? 0 : Date.now();
        timeout = null;
        result = func.apply(context, args);
    };
    
    const throttled = function(...args) {
        const now = Date.now();
        
        if (!previous && options.leading === false) {
            previous = now;
        }
        
        const remaining = limit - (now - previous);
        
        if (remaining <= 0 || remaining > limit) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            result = func.apply(this, args);
        } else if (!timeout && options.trailing !== false) {
            timeout = setTimeout(() => later(this, args), remaining);
        }
        
        return result;
    };
    
    throttled.cancel = function() {
        clearTimeout(timeout);
        previous = 0;
        timeout = null;
    };
    
    return throttled;
}

// Usage examples
const debouncedSearch = debounce(function(query) {
    console.log('Searching for:', query);
    // Perform search
}, 300);

const throttledScroll = throttle(function(event) {
    console.log('Scroll position:', window.scrollY);
    // Update UI based on scroll
}, 100);

// Event listeners
document.getElementById('search').addEventListener('input', (e) => {
    debouncedSearch(e.target.value);
});

window.addEventListener('scroll', throttledScroll);
\`\`\``,
      practice: {
        task: 'Create a performance-optimized data visualization component',
        starter: `// Create a data visualization component that:
// 1. Efficiently handles large datasets (10,000+ items)
// 2. Uses virtual scrolling for performance
// 3. Implements lazy loading for data
// 4. Includes performance monitoring
// 5. Optimizes memory usage

// Add your implementation here`,
        hints: [
          'Use virtual scrolling to render only visible items',
          'Implement lazy loading for data chunks',
          'Use requestAnimationFrame for smooth animations',
          'Monitor performance with custom metrics',
          'Implement cleanup methods to prevent memory leaks'
        ]
      }
    }
  ]
};
